import { getMomentTz } from "@dexteel/mesf-core";
import AlarmIcon from "@mui/icons-material/Alarm";
import GroupsIcon from "@mui/icons-material/Groups";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	alpha,
	Box,
	PaletteColor,
	Theme,
	Typography,
	useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { OverviewBreak } from "../models/types";

const moment = getMomentTz();

// Server-time driven (like ShiftMetricsCard's Next Stop): no local ticker,
// the overview's 10s refetch keeps it fresh.
const minutesUntil = (target: Date, now: Date) =>
	Math.max(0, Math.ceil(moment(target).diff(moment(now), "minutes", true)));

const fmtWait = (minutes: number) => {
	if (minutes < 60) return `in ${minutes} min`;
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return m > 0 ? `in ${h}h ${m}m` : `in ${h}h`;
};

const hhmm = (d: Date) => moment(d).format("HH:mm");

type Props = {
	breaks: OverviewBreak[] | undefined;
	serverTime: Date | null | undefined;
	isRunning: boolean;
};

type BreakState =
	| { kind: "upcoming" | "active" | "restart"; brk: OverviewBreak; now: Date }
	| { kind: "crew" }
	| { kind: "done" };

// Spells never announced; all-spell shift = spelling crew. Rows ordered by Start.
const resolveBreakState = ({
	breaks,
	serverTime: now,
	isRunning,
}: Props): BreakState | null => {
	if (!breaks?.length || !now) return null;
	const stops = breaks.filter((b) => !b.IsSpell);
	if (!stops.length) return { kind: "crew" };
	const active = stops.find((b) => b.Start <= now && now < b.End);
	if (active) return { kind: "active", brk: active, now };
	// RanAfter keeps a later stop from raising the restart again
	const ended = stops.findLast((b) => b.End <= now);
	if (ended && !ended.RanAfter && !isRunning)
		return { kind: "restart", brk: ended, now };
	const upcoming = stops.find((b) => b.Start > now);
	return upcoming ? { kind: "upcoming", brk: upcoming, now } : { kind: "done" };
};

const accentTone = (accent: PaletteColor, bgAlpha: number) => ({
	bg: alpha(accent.light, bgAlpha),
	border: accent.main,
	icon: accent.dark,
	text: accent.dark,
});

const pillTone = (theme: Theme, kind: BreakState["kind"]) => {
	if (kind === "active") return accentTone(theme.palette.warning, 0.27);
	if (kind === "restart") return accentTone(theme.palette.error, 0.22);
	if (kind === "crew") return accentTone(theme.palette.info, 0.1);
	return {
		bg: theme.palette.grey[100],
		border: theme.palette.grey[400],
		icon: theme.palette.grey[600],
		text: theme.palette.grey[700],
	};
};

const stateIcon = (kind: BreakState["kind"]) =>
	kind === "restart"
		? RestartAltIcon
		: kind === "crew"
			? GroupsIcon
			: AlarmIcon;

const pillText = (state: BreakState) => {
	if (state.kind === "crew") return "4-man crew · spelling, no stops";
	if (state.kind === "done") return "No breaks left this shift";
	const { brk, now } = state;
	if (state.kind === "restart")
		return `Restart · ${brk.BreakLabel} ended ${hhmm(brk.End)}`;
	if (state.kind === "active")
		return `${brk.BreakLabel} — ends ${hhmm(brk.End)} · ${minutesUntil(brk.End, now)} min left`;
	return `${brk.BreakLabel} · ${hhmm(brk.Start)} ${fmtWait(minutesUntil(brk.Start, now))}`;
};

/** Header pill. Hidden when the shift has no breaks. */
export const BreakCountdownPill = (props: Props) => {
	const theme = useTheme();
	const state = resolveBreakState(props);
	if (!state) return null;

	const tone = pillTone(theme, state.kind);
	const Icon = stateIcon(state.kind);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				px: 1.25,
				py: 0.5,
				borderRadius: 1,
				backgroundColor: tone.bg,
				border: `1px solid ${tone.border}`,
			}}
		>
			<Icon sx={{ fontSize: "1rem", color: tone.icon }} />
			<Typography
				sx={{
					fontSize: "0.8rem",
					fontWeight: 600,
					lineHeight: 1,
					fontVariantNumeric: "tabular-nums",
					color: tone.text,
				}}
			>
				{pillText(state)}
			</Typography>
		</Box>
	);
};

/** Banner on a running stop and on restart: the wall screens' start/end notification. */
export const BreakBannerRow = (props: Props) => {
	const theme = useTheme();
	const state = resolveBreakState(props);
	if (state?.kind !== "active" && state?.kind !== "restart") return null;
	const { brk, now } = state;
	const tone = pillTone(theme, state.kind);
	const Icon = stateIcon(state.kind);

	return (
		<Grid2 size={{ xs: 12 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 1,
					px: 2,
					py: 0.75,
					borderRadius: 1,
					backgroundColor: tone.bg,
					border: `1px solid ${tone.border}`,
					color: tone.text,
				}}
			>
				<Icon />
				<Typography
					sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
				>
					{state.kind === "restart"
						? `${brk.BreakLabel} over — restart the line · ${moment(now).diff(brk.End, "minutes")} min past ${hhmm(brk.End)}`
						: `${brk.BreakLabel} — ${hhmm(brk.Start)} to ${hhmm(brk.End)} · ${minutesUntil(brk.End, now)} min left`}
				</Typography>
			</Box>
		</Grid2>
	);
};
